/* eslint-env browser */
// Driver documents › Yearly tax (wave 3, W2_Portal_Tables look): per-driver
// totals for a fixed Ethiopian tax year (8 Jul – 7 Jul), in the modern
// table, with the tax year and "Generate report" at the top.
//
// Data, exactly as before:
// - Opening the tab (or picking a year) shows the session cache, else reads
//   ONLY the saved snapshot (get yearlyTaxReportRef(year)); no bookings scan.
// - "Generate report" (admin / finance) runs the one bounded range read of
//   the year's bookings (bookingsByDateRangeRef, or the tripdateMs index
//   when settings.useTripdateMsIndex is on), aggregates per driver, shows
//   the result at once, then saves it with set(yearlyTaxReportRef(year)).
// - REPORT_CACHE is module level on purpose: the Driver documents host
//   unmounts inactive tabs, so a ref would be wiped on every tab switch.
// Read-only list: no editable dates, no driver changes. CSV for admin and
// finance only, as before (plain rounded numbers in the money columns).
import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { get, set } from 'firebase/database';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { FirebaseContext } from 'common';
import { tokens as c } from '../theme';
import { InlineMessage } from '../components/feedback';
import { formatAddisDateTime } from '../common/exportUtils';
import { DataTable, PageHeader, calmCaps } from '../components/modern';
import { taxYearBounds, currentTaxYear, taxYearOptions } from '../utils/ethiopianTaxYear';

// Module-level cache (NOT a ref): the Driver Documents container unmounts the
// inactive tab, so a component-scoped ref would be wiped on every tab switch.
// Keyed by year → the loaded/generated report; survives unmount within the page
// session so re-opening the tab is instant and never re-runs.
const REPORT_CACHE = {};

// tripdate arrives as epoch-ms (number / all-digit string) or an ISO string
// depending on the booking's creation path — normalize to epoch ms.
const parseTs = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const str = String(value).trim();
  if (/^\d+$/.test(str)) return Number(str);
  const ms = new Date(str).getTime();
  return Number.isNaN(ms) ? null : ms;
};

// Group COMPLETE/PAID bookings in the [fromTs,toTs] window by driver and tally
// the taxable figure (cash collected) plus context aggregates.
const buildRows = (bookingsObj, fromTs, toTs) => {
  const byDriver = {};
  Object.keys(bookingsObj || {}).forEach((id) => {
    const b = bookingsObj[id];
    if (!b || !b.driver) return;
    if (b.status !== 'COMPLETE' && b.status !== 'PAID') return;
    const ts = parseTs(b.tripdate);
    if (ts === null || ts < fromTs || ts > toTs) return; // exact-window re-filter
    let row = byDriver[b.driver];
    if (!row) {
      row = byDriver[b.driver] = { driverId: b.driver, driverName: b.driver_name || '-', trips: 0, taxable: 0, gross: 0, commission: 0, earnings: 0 };
    }
    row.trips += 1;
    row.taxable += parseFloat(b.cashPaymentAmount) || 0; // Debit / cash collected
    row.gross += parseFloat(b.trip_cost) || 0;
    row.commission += parseFloat(b.convenience_fees) || 0;
    row.earnings += parseFloat(b.driver_share) || 0;
    if ((!row.driverName || row.driverName === '-') && b.driver_name) row.driverName = b.driver_name;
  });
  return Object.values(byDriver).sort((a, b) => b.taxable - a.taxable);
};

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

// Tab 4 of the Driver Documents centre: per-driver totals for a fixed Ethiopian
// tax year (8 Jul – 7 Jul). Read-only list, year filter only — no editable dates
// and no driver deactivation. The heavy per-year bookings scan runs ONLY on an
// explicit Generate action; the aggregate is cached for the session and
// persisted to RTDB so reopening never re-scans.
export default function YearlyTaxReport() {
  const { t, i18n } = useTranslation();
  const firebase = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);

  const role = auth.profile?.usertype;
  const canGenerate = role === 'admin' || role === 'finance';

  const [selectedYear, setSelectedYear] = useState(currentTaxYear());
  const [report, setReport] = useState(null); // { rows, generatedAt, generatedBy, ... } | null
  const [loading, setLoading] = useState(true); // reading the saved snapshot
  const [generating, setGenerating] = useState(false); // running the heavy scan
  const [error, setError] = useState('');

  const label = (key, fallback, opts) => t(key, { defaultValue: fallback, ...(opts || {}) });
  const yearOptions = taxYearOptions(6);
  const bounds = taxYearBounds(selectedYear);

  const decimals = settings && settings.decimal != null ? settings.decimal : 2;
  const round = (n) => Math.round((Number(n) + Number.EPSILON) * Math.pow(10, decimals)) / Math.pow(10, decimals);
  // Pretty money: thousands separators + fixed decimals, e.g. "1,234.00".
  const fmtNum = (n) => Number(round(n)).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const money = (n) => (settings?.swipe_symbol ? `${fmtNum(n)} ${settings.symbol || ''}` : `${settings?.symbol || ''} ${fmtNum(n)}`).trim();

  // On year change (and mount): use the session cache, else load the SAVED
  // snapshot only — no bookings scan.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setError('');
      if (REPORT_CACHE[selectedYear] !== undefined) {
        setReport(REPORT_CACHE[selectedYear]);
        setLoading(false);
        return;
      }
      if (!firebase || !firebase.yearlyTaxReportRef) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const snap = await get(firebase.yearlyTaxReportRef(selectedYear));
        const val = snap.val();
        const rep = val && Array.isArray(val.rows) ? val : null;
        REPORT_CACHE[selectedYear] = rep;
        if (!cancelled) setReport(rep);
      } catch (e) {
        console.error('Failed to load saved tax report:', e);
        if (!cancelled) setReport(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedYear, firebase]);

  // Explicit, on-demand: scan the year's bookings, aggregate, cache + persist.
  const handleGenerate = async () => {
    if (!firebase || !firebase.bookingsByDateRangeRef) return;
    setGenerating(true);
    setError('');
    try {
      const { fromTs, toTs } = taxYearBounds(selectedYear);
      // settings.useTripdateMsIndex: query the server-stamped numeric
      // tripdateMs index, which also covers ISO-string-tripdate (Donkey Meter)
      // trips that the mixed-type tripdate range query silently excludes.
      // Flag off / ref absent = the legacy query, byte-identical.
      const useMsIndex = settings && settings.useTripdateMsIndex === true && typeof firebase.bookingsByDateRangeMsRef === 'function';
      const rangeRef = useMsIndex ? firebase.bookingsByDateRangeMsRef : firebase.bookingsByDateRangeRef;
      const snap = await get(rangeRef(fromTs, toTs));
      const rows = buildRows(snap.val() || {}, fromTs, toTs).map((r) => ({
        ...r,
        taxable: round(r.taxable),
        gross: round(r.gross),
        commission: round(r.commission),
        earnings: round(r.earnings),
      }));
      const profile = auth.profile || {};
      const rep = {
        year: selectedYear,
        generatedAt: new Date().getTime(),
        generatedBy: profile.email || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.uid || null,
        driverCount: rows.length,
        totalTaxable: round(rows.reduce((s, r) => s + (r.taxable || 0), 0)),
        rows,
      };
      // Cache + show immediately (survives tab switches), THEN best-effort persist.
      REPORT_CACHE[selectedYear] = rep;
      setReport(rep);
      if (firebase.yearlyTaxReportRef) {
        try {
          await set(firebase.yearlyTaxReportRef(selectedYear), rep);
        } catch (persistErr) {
          // Persistence is for cross-reload/cross-user; failing it must not lose
          // the in-session report. Most likely the /yearly_tax_reports DB rule
          // isn't deployed yet.
          console.warn('Yearly tax report computed but not persisted (deploy /yearly_tax_reports rule):', persistErr);
        }
      }
    } catch (e) {
      console.error('Failed to generate tax report:', e);
      setError(label('tax_generate_failed', 'Failed to generate the report. Please try again.'));
    } finally {
      setGenerating(false);
    }
  };

  const rows = report && Array.isArray(report.rows) ? report.rows : [];
  const driverCount = report ? (report.driverCount != null ? report.driverCount : rows.length) : 0;
  const totalTaxable = report && report.totalTaxable != null ? report.totalTaxable : 0;

  const columns = [
    {
      id: 'driver',
      title: label('driver_name', 'Driver'),
      field: 'driverName',
      render: (r) =>
        r.driverId ? (
          <Link
            href={`/users/driverdetails/${r.driverId}`}
            target="_blank"
            rel="noopener noreferrer"
            title={label('view_driver', 'View driver')}
            sx={{ color: c.ACCENT_TEXT, fontWeight: 600, textDecorationColor: 'currentColor', overflowWrap: 'anywhere' }}
          >
            {r.driverName || '-'}
          </Link>
        ) : (
          r.driverName || '-'
        ),
    },
    { title: label('trips', 'Trips'), field: 'trips', type: 'number' },
    {
      title: label('taxable_amount', 'Taxable (Cash Collected)'),
      field: 'taxable',
      type: 'money',
      defaultSort: 'desc',
      render: (r) => (
        <Box component="span" sx={{ fontWeight: 700 }}>
          {money(r.taxable)}
        </Box>
      ),
    },
    { title: label('gross_fare', 'Gross Fare'), field: 'gross', type: 'money', render: (r) => money(r.gross) },
    { title: label('commission', 'Commission'), field: 'commission', type: 'money', render: (r) => money(r.commission) },
    { title: label('driver_earnings', 'Driver Earnings'), field: 'earnings', type: 'money', render: (r) => money(r.earnings) },
  ];

  // The same CSV as before: every column, plain rounded numbers in the money
  // columns (no thousands separators).
  const exportColumns = [
    { title: label('driver_name', 'Driver'), field: 'driverName' },
    { title: label('trips', 'Trips'), field: 'trips' },
    { title: label('taxable_amount', 'Taxable (Cash Collected)'), value: (r) => round(r.taxable) },
    { title: label('gross_fare', 'Gross Fare'), value: (r) => round(r.gross) },
    { title: label('commission', 'Commission'), value: (r) => round(r.commission) },
    { title: label('driver_earnings', 'Driver Earnings'), value: (r) => round(r.earnings) },
  ];

  const yearLabel = calmCaps(label('tax_year', 'Tax Year'));
  const tableTitle = `${label('yearly_tax_title', 'Yearly Tax (Ethiopia)')} — ${bounds.label}`;
  const generatedLine =
    report && report.generatedAt
      ? `${label('generated_on', 'Generated')} ${formatAddisDateTime(report.generatedAt)}${report.generatedBy ? ` · ${label('by', 'by')} ${report.generatedBy}` : ''}`
      : '';

  const countText = ({ shown, total, query, loading: busy }) => {
    if (busy) return label('loading', 'Loading…');
    if (!report) return `${yearLabel} ${bounds.label}`;
    if (query) return label('table_count_shown', '{{shown}} of {{total}}', { shown: fmt(shown), total: fmt(total) });
    return t('tax_report_count', {
      count: driverCount,
      n: fmt(driverCount),
      total: money(totalTaxable),
      defaultValue_one: '{{n}} driver · {{total}} taxable',
      defaultValue_other: '{{n}} drivers · {{total}} taxable',
      defaultValue: '{{n}} drivers · {{total}} taxable',
    });
  };

  const primaryLabel = generating
    ? label('generating', 'Generating…')
    : report
      ? label('regenerate', 'Regenerate')
      : label('generate_report', 'Generate report');

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        primaryAction={{
          label: primaryLabel,
          icon: report ? <AutorenewRoundedIcon /> : <AssessmentOutlinedIcon />,
          onClick: handleGenerate,
          disabled: loading,
          busy: generating,
          show: canGenerate,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="label" htmlFor="tax-year-select" sx={{ fontSize: 14.5, fontWeight: 600, color: c.INK_2, whiteSpace: 'nowrap' }}>
            {yearLabel}
          </Typography>
          <TextField
            id="tax-year-select"
            select
            size="small"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            sx={{ minWidth: { xs: 200, sm: 260 } }}
          >
            {yearOptions.map((o) => (
              <MenuItem key={o.year} value={o.year}>
                {o.label} ({o.range})
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </PageHeader>

      {error ? (
        <InlineMessage kind="error" onClose={() => setError('')}>
          {error}
        </InlineMessage>
      ) : null}

      <DataTable
        title={tableTitle}
        rows={rows}
        columns={columns}
        rowKey="driverId"
        loading={loading}
        refreshing={generating}
        countText={countText}
        subtitle={generatedLine || undefined}
        searchPlaceholder={label('tax_search_driver', 'Search driver name')}
        prefsKey="yearly_tax"
        exportCsv={canGenerate}
        exportColumns={exportColumns}
        exportFileName={`yearly_tax_${bounds.label.replace('/', '_')}`}
        exportTitle={tableTitle}
        minWidth={760}
        emptyState={({ query }) => {
          if (query) return null;
          if (report) {
            return { icon: <AssessmentOutlinedIcon />, title: label('tax_report_no_trips', 'No completed trips in this tax year') };
          }
          return {
            icon: <AssessmentOutlinedIcon />,
            title: `${label('no_saved_tax_report', 'No saved report for')} ${bounds.range}.`,
            body: canGenerate
              ? label('tax_generate_hint', 'Click Generate Report to scan this tax year and save the result.')
              : label('tax_ask_admin', 'Ask an admin or finance user to generate this report.'),
          };
        }}
      />
    </Box>
  );
}
